import { useMemo, useState } from "react";
import { Check } from "lucide-react";
import { de, formatQuantity, guessShop, listeFuerLaden, type List, type Store, type WeekPlanResult } from "@ainkauf/core";

/**
 * Was gleich auf der Liste landet, bevor es dort landet.
 *
 * Eine Woche bringt schnell zwanzig Artikel mit. Ohne diesen Blick dazwischen
 * wäre die Liste hinterher voll und niemand wüsste, woher was kam. Deshalb
 * steht an jeder Zeile, aus welchen Rezepten sie stammt, und was der Vorrat
 * schon abdeckt, steht daneben statt zu verschwinden.
 *
 * Seit dem 20.09.2026 wird hier gewählt, und seit dem 21.09.2026 andersherum:
 * **Vorgewählt ist nichts.** Daniel: "Man sagt halt selbst, was man von dem,
 * was in dem Rezept angegeben wird, mit reintut." Das ist die ehrlichere
 * Richtung, denn der Vorrat in der App weiß nur, was jemand gepflegt hat,
 * und die Küche zu Hause weiß es besser. "Alle" bleibt der Weg für den, der
 * wirklich alles braucht.
 *
 * Was ohnehin im Haus ist, steht gar nicht erst zur Wahl (siehe guessStaple),
 * und Leitungswasser wird nicht einmal erwähnt (istKeineWare).
 */
export function PlanPreview({
  plan,
  lists,
  stores = [],
  onConfirm,
  onCancel,
}: {
  plan: WeekPlanResult;
  lists: readonly List[];
  /** Für die Anzeige, in welche Liste eine Zeile wandert. */
  stores?: readonly Store[];
  /** Bestätigt wird mit der Zielliste und genau den Zeilen, die gewählt sind. */
  onConfirm: (listId: string, zeilen: WeekPlanResult["missing"]) => Promise<void>;
  onCancel: () => void;
}) {
  const [ziel, setZiel] = useState(
    // Die Standardliste der Familie, nicht die erste der Sortierung: Die hieß
    // bei Daniel "Einkauf", eingekauft wird aber auf "Supermarkt".
    () => (lists.find((liste) => liste.is_default) ?? lists[0])?.id ?? "",
  );
  const [angewaehlt, setAngewaehlt] = useState<ReadonlySet<string>>(new Set());
  const [busy, setBusy] = useState(false);

  // Die Zeilen tragen keine eigene Kennung, sie entstehen beim Rechnen. Name
  // und Position zusammen sind eindeutig genug für eine Auswahl im Blatt.
  const schluessel = (zeile: { normalized_name: string }, i: number) => `${zeile.normalized_name}-${String(i)}`;
  const gewaehlt = useMemo(
    () => plan.missing.filter((zeile, i) => angewaehlt.has(schluessel(zeile, i))),
    [plan.missing, angewaehlt],
  );

  // Wohin eine Zeile wandert, wenn nicht in die gewählte Liste. Nur aus der
  // Standardliste heraus wird verteilt, genau wie bei der Schnelleingabe.
  const gewaehlteListe = lists.find((liste) => liste.id === ziel) ?? null;
  const andereListe = (zeile: WeekPlanResult["missing"][number]) => {
    if (gewaehlteListe?.is_default !== true) return null;
    const treffer = listeFuerLaden(guessShop({ name: zeile.name, category: zeile.category }), lists, stores);
    return treffer && treffer.id !== ziel ? treffer.name : null;
  };

  const umschalten = (key: string) => {
    setAngewaehlt((bisher) => {
      const neu = new Set(bisher);
      if (neu.has(key)) neu.delete(key);
      else neu.add(key);
      return neu;
    });
  };

  return (
    <div className="fixed inset-0 z-40 flex flex-col justify-end bg-ink/40" onClick={onCancel} role="presentation">
      <div
        onClick={(event) => {
          event.stopPropagation();
        }}
        role="dialog"
        aria-label={de.planning.preview}
        data-testid="plan-preview"
        className="flex max-h-[85dvh] flex-col gap-4 overflow-y-auto rounded-t-[var(--radius-xl)] bg-paper px-5 pt-5"
        style={{ paddingBottom: "calc(20px + env(safe-area-inset-bottom, 0px))" }}
      >
        <span aria-hidden className="mx-auto mb-1 h-1.5 w-12 rounded-full bg-paper-3" />
        <h2 className="text-xl">{de.planning.preview}</h2>

        {plan.missing.length === 0 ? (
          <p className="karte px-4 py-6 text-center text-ink-2" data-testid="plan-nothing">
            {de.planning.nothingMissing}
          </p>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="num text-sm font-semibold text-ink-2" data-testid="plan-missing-count">
                {de.planning.chosen(gewaehlt.length, plan.missing.length)}
              </span>
              <span className="flex gap-1.5">
                <button
                  type="button"
                  onClick={() => {
                    setAngewaehlt(new Set(plan.missing.map((zeile, i) => schluessel(zeile, i))));
                  }}
                  data-testid="plan-all"
                  className="min-h-[44px] rounded-full bg-paper-2 px-4 text-sm font-semibold text-ink-2"
                >
                  {de.planning.all}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAngewaehlt(new Set());
                  }}
                  data-testid="plan-none"
                  className="min-h-[44px] rounded-full bg-paper-2 px-4 text-sm font-semibold text-ink-2"
                >
                  {de.planning.none}
                </button>
              </span>
            </div>
            <p className="text-sm text-ink-3">{de.planning.chooseHint}</p>
            <ul className="flex flex-col gap-1.5">
              {plan.missing.map((zeile, i) => {
                const key = schluessel(zeile, i);
                const an = angewaehlt.has(key);
                return (
                  <li key={key}>
                    <button
                      type="button"
                      onClick={() => {
                        umschalten(key);
                      }}
                      aria-pressed={an}
                      data-testid="plan-item"
                      data-name={zeile.name}
                      data-chosen={an ? "ja" : "nein"}
                      // Nicht gewählt heißt hier "noch nicht", nicht "weg".
                      // Deshalb bleibt die Zeile voll lesbar; gewählt wird sie
                      // durch den Rahmen in der Akzentfarbe kenntlich, nicht
                      // durch Ausgrauen der anderen.
                      //
                      // outline statt ring: .karte setzt einen box-shadow, und
                      // Tailwinds ring arbeitet ebenfalls über box-shadow. Der
                      // Ring fiel deshalb still aus, ohne Fehler, ohne Warnung.
                      className={`karte flex min-h-[60px] w-full items-center gap-3 px-4 text-left transition-transform active:scale-[0.99] ${
                        an ? "outline-2 outline-accent" : ""
                      }`}
                    >
                      <span
                        aria-hidden
                        className={`inline-grid size-7 shrink-0 place-items-center rounded-full border-2 ${
                          an ? "border-accent bg-accent text-white" : "border-paper-3 text-transparent"
                        }`}
                      >
                        <Check size={16} strokeWidth={3} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium">{zeile.name}</span>
                        {zeile.recipes.length > 0 && (
                          <span className="block truncate text-sm text-ink-2">{de.planning.fromRecipes(zeile.recipes.join(", "))}</span>
                        )}
                        {andereListe(zeile) !== null && (
                          <span className="block truncate text-sm font-semibold text-accent" data-testid="plan-item-liste">
                            {de.planning.goesTo(andereListe(zeile) ?? "")}
                          </span>
                        )}
                      </span>
                      <span className="num shrink-0 font-semibold text-ink-2">
                        {formatQuantity({ quantity: zeile.quantity, unit: zeile.unit })}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </>
        )}

        {plan.covered.length > 0 && (
          <section className="flex flex-col gap-1.5" data-testid="plan-covered">
            <span className="text-sm font-semibold text-herb">{de.planning.covered(plan.covered.length)}</span>
            <ul className="flex flex-wrap gap-1.5">
              {plan.covered.map((zeile, i) => (
                <li
                  key={`${zeile.normalized_name}-${String(i)}`}
                  className="inline-flex items-center gap-1 rounded-full bg-paper-2 px-3 py-1 text-sm text-ink-2"
                >
                  <Check size={14} strokeWidth={2.5} className="text-herb" aria-hidden />
                  {zeile.name}
                </li>
              ))}
            </ul>
          </section>
        )}

        {plan.staples.length > 0 && (
          <section className="flex flex-col gap-1">
            <span className="text-sm text-ink-3">{de.planning.staples}</span>
            <span className="text-sm text-ink-2">{plan.staples.join(", ")}</span>
          </section>
        )}

        {plan.missing.length > 0 && (
          <label className="flex flex-col gap-1.5 text-sm text-ink-2">
            {de.planning.targetList}
            <select
              value={ziel}
              onChange={(event) => {
                setZiel(event.target.value);
              }}
              data-testid="plan-list"
              className="min-h-[52px] rounded-[var(--radius-lg)] bg-paper-2 px-4 text-base text-ink outline-none focus:ring-2 focus:ring-accent"
            >
              {lists.map((liste) => (
                <option key={liste.id} value={liste.id}>
                  {liste.name}
                </option>
              ))}
            </select>
          </label>
        )}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="min-h-[52px] flex-1 rounded-[var(--radius-lg)] bg-paper-2 font-semibold text-ink-2"
          >
            {de.common.cancel}
          </button>
          <button
            type="button"
            disabled={busy || gewaehlt.length === 0 || ziel.length === 0}
            onClick={() => {
              setBusy(true);
              void onConfirm(ziel, gewaehlt);
            }}
            data-testid="plan-confirm"
            className="min-h-[52px] flex-1 rounded-[var(--radius-lg)] bg-accent font-display text-base font-extrabold text-white disabled:opacity-40"
          >
            {plan.missing.length === 0 ? de.planning.confirm : de.planning.confirmCount(gewaehlt.length)}
          </button>
        </div>
      </div>
    </div>
  );
}
